import { useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { useAuthStore } from '../store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';

export function useAuth() {
  const { setUser, setLoading, clearAuth, restoreUser } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isSurveyPath = window.location.hostname === 'sondage.cs-expo1.fr';
  const authInitializedRef = useRef(false);

  const updateUserOnlineStatus = useCallback(async (userId: string, isOnline: boolean) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      await setDoc(userDocRef, {
        isOnline,
        lastSeen: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating online status:', error);
    }
  }, []);

  const handleAuthStateChange = useCallback(async (firebaseUser: any) => {
    try {
      if (firebaseUser) {
        // Try to restore user from store first
        let userData = restoreUser();
        
        if (!userData) {
          // If not in store, fetch from Firestore
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            userData = {
              id: firebaseUser.uid,
              ...data,
              createdAt: data.createdAt?.toDate(),
              updatedAt: data.updatedAt?.toDate(),
              lastSeen: new Date()
            };
          } else {
            throw new Error('User document not found');
          }
        }

        // Update online status
        await updateUserOnlineStatus(firebaseUser.uid, true);

        // Set user in store
        setUser(userData);

        // Handle redirections
        if (location.pathname.startsWith('/auth')) {
          const redirectTo = userData.role === 'admin' || userData.role === 'council'
            ? '/admin'
            : '/';
          navigate(redirectTo, { replace: true });
        }
      } else if (!location.pathname.startsWith('/auth') && location.pathname !== '/drive' && !location.pathname.startsWith('/legal')) {
        await clearAuth();
        navigate('/auth', { replace: true });
      }
    } catch (error) {
      console.error('Error in auth state change:', error);
      await clearAuth();
      if (location.pathname !== '/drive' && !location.pathname.startsWith('/legal')) {
        navigate('/auth', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading, clearAuth, navigate, location, updateUserOnlineStatus, restoreUser]);

  useEffect(() => {
    let unsubscribeAuth: (() => void) | undefined;

    const initializeAuth = async () => {
      if (authInitializedRef.current) return;
      authInitializedRef.current = true;

      try {
        if (isSurveyPath) {
          setLoading(false);
          return;
        }

        // Set persistent auth BEFORE listening to auth state
        await setPersistence(auth, browserLocalPersistence);

        // Listen for auth state changes
        unsubscribeAuth = onAuthStateChanged(auth, handleAuthStateChange);
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
      }
    };

    initializeAuth();

    // Cleanup function
    return () => {
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }

      // Update offline status when component unmounts
      if (auth.currentUser) {
        updateUserOnlineStatus(auth.currentUser.uid, false);
      }
    };
  }, [handleAuthStateChange, setLoading, isSurveyPath, updateUserOnlineStatus]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!auth.currentUser) return;

      if (document.visibilityState === 'hidden') {
        updateUserOnlineStatus(auth.currentUser.uid, false);
      } else {
        updateUserOnlineStatus(auth.currentUser.uid, true);
      }
    };

    // Update status when tab becomes visible/hidden
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Set up ping interval to keep online status accurate
    const pingInterval = setInterval(() => {
      if (auth.currentUser && document.visibilityState === 'visible') {
        updateUserOnlineStatus(auth.currentUser.uid, true);
      }
    }, 60000); // Ping every minute

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(pingInterval);
    };
  }, [updateUserOnlineStatus]);

  // Handle page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (auth.currentUser) {
        // Use navigator.sendBeacon for reliable delivery during page unload
        const data = new Blob([JSON.stringify({ userId: auth.currentUser.uid })], {
          type: 'application/json'
        });
        navigator.sendBeacon('/api/offline', data);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
}